import TextField, { type TextFieldProps } from "@mui/material/TextField";
import React, { type ChangeEvent, type JSX } from "react";

import { areBoundFieldPropsEqual } from "./MemoHelper";

import type { BoundedValue, ErrorMap } from "@repo/shared/common";

export type PureBoundTextFieldProps = {
	value: string;
	onChangeValue: (value: string) => void;

	label: string;
	errors?: ErrorMap;
	errorKey?: string;

	additionalProps?: Omit<TextFieldProps, "value" | "onChange" | "label" | "error" | "helperText">;
};

const PureBoundTextField = ({
	value,
	onChangeValue,
	label,
	errors,
	errorKey,
	additionalProps,
}: PureBoundTextFieldProps): JSX.Element => {
	const key = errorKey;
	const errorMessage = key != null ? errors?.[key] : undefined;

	const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
		onChangeValue(event.target.value ?? "");
	};

	return (
		<TextField
			value={value}
			onChange={handleChange}
			label={label}
			error={Boolean(errorMessage)}
			helperText={errorMessage}
			{...additionalProps}
		/>
	);
};

// Memoized version: re-render only if relevant props changed
const MemoBoundTextField = React.memo(PureBoundTextField, areBoundFieldPropsEqual);

export function BoundTextField(
	props: {
		boundedValue: BoundedValue<string>;
	} & Omit<PureBoundTextFieldProps, "value" | "onChangeValue" | "errorKey" | "errors">,
): JSX.Element {
	return (
		<MemoBoundTextField
			{...props}
			value={props.boundedValue.value ?? ""}
			onChangeValue={(v) => {
				props.boundedValue.onChangeValue(v);
			}}
			errorKey={props.boundedValue.errorKey}
			errors={props.boundedValue.errorMap}
		/>
	);
}
